import * as React from "react"

import { cn } from "@/lib/utils"

const Input = React.forwardRef(({ className, type, value, onChange, onBlur, noDebounce, ...props }, ref) => {
  const isFile = type === 'file';
  const [localVal, setLocalVal] = React.useState(value ?? '');
  const onChangeRef = React.useRef(onChange);
  const timerRef = React.useRef(null);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    setLocalVal(value ?? '');
  }, [value]);

  React.useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleChange = React.useCallback((e) => {
    const val = e.target.value;
    const name = e.target.name;
    setLocalVal(val);
    // File inputs and noDebounce inputs forward the real event immediately
    // (file inputs need e.target.files which a synthetic event would not carry)
    if (noDebounce || isFile) {
      if (onChangeRef.current) onChangeRef.current(e);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (onChangeRef.current) onChangeRef.current({ target: { value: val, name } });
    }, 150);
  }, [noDebounce, isFile]);

  const handleBlur = React.useCallback((e) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (onChangeRef.current && localVal !== (value ?? '')) {
      onChangeRef.current({ target: { value: localVal, name: e.target.name } });
    }
    if (onBlur) onBlur(e);
  }, [localVal, value, onBlur]);

  return (
    <input
      type={type}
      {...(isFile ? {} : { value: localVal })}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
